import { useState, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import { validateDomain, DOMAIN_STATUS_LABELS, SSL_STATUS_LABELS } from '@/types/domains';
import type { DomainUIData, DomainStatus, DnsRecord } from '@/types/domains';
import { getDomainStatus, requestDomain, verifyDomain, setPrimaryDomain, removeDomain } from '@/services/domains';

export default function SettingsDomainTab() {
  const { canAccess, currentPlan } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();
  const hasDomainFeature = canAccess('custom_domain');

  const [domainData, setDomainData] = useState<DomainUIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [radioId] = useState<string>('1');

  // Cargar datos reales al montar
  useEffect(() => {
    if (!hasDomainFeature) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const data = await getDomainStatus(radioId);
        if (!cancelled) {
          setDomainData(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // Fallback: si hay error y domainData es null, mostrar estado vacío
          if (!domainData) {
            const empty = await getDomainStatus(radioId);
            if (!cancelled) setDomainData(empty);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [hasDomainFeature, radioId]);

  // ── HANDLERS ──

  const handleRequestDomain = useCallback(async () => {
    const validation = validateDomain(newDomain);
    if (!validation.valid) {
      Swal.fire({
        title: 'Dominio inválido',
        text: validation.error || 'Revisá el formato del dominio.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        },
        buttonsStyling: false,
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await requestDomain(radioId, validation.sanitized!);
      setDomainData(result);
      setNewDomain('');

      Swal.fire({
        title: 'Dominio registrado',
        html: `<div style="text-align:left;font-size:13px"><p style="margin-bottom:10px">Se registró <strong>${validation.sanitized}</strong> para tu radio.</p><div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px"><p style="font-weight:600;color:#92400e;margin:0 0 4px;font-size:11px"><i class="ri-information-line"></i> Paso siguiente</p><p style="color:#92400e;margin:0;font-size:11px">Configurá los registros DNS en tu proveedor de dominio.</p></div></div>`,
        icon: 'success',
        confirmButtonText: 'Ver instrucciones DNS',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al solicitar dominio';
      Swal.fire({
        title: 'Error',
        text: msg,
        icon: 'error',
        confirmButtonText: 'Cerrar',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        },
        buttonsStyling: false,
      });
    } finally {
      setSubmitting(false);
    }
  }, [newDomain, radioId]);

  const handleVerifyDns = useCallback(async () => {
    if (!domainData?.domainId) return;

    setVerifying(true);
    try {
      const result = await verifyDomain(radioId, domainData.domainId);
      const newStatus: DomainStatus = result.verified ? 'VERIFIED' : 'DNS_REQUIRED';

      setDomainData((prev) => prev ? { ...prev, status: newStatus, errorMessage: result.verified ? null : result.message } : prev);

      if (result.verified) {
        Swal.fire({
          title: 'DNS Verificado',
          html: `<div style="text-align:left;font-size:13px"><p style="margin-bottom:8px">Todos los registros DNS de <strong>${domainData.domain}</strong> están correctos.</p><div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px"><p style="font-weight:600;color:#166534;margin:0;font-size:11px"><i class="ri-check-line"></i> ${result.records_passing}/${result.records_checked} registros verificados</p></div></div>`,
          icon: 'success',
          confirmButtonText: 'Activar dominio',
          showCancelButton: true,
          cancelButtonText: 'Después',
          customClass: {
            popup: 'rounded-2xl',
            title: 'font-bold text-sm',
            confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
            cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600',
            htmlContainer: 'text-sm',
          },
          buttonsStyling: false,
        }).then((swalResult) => {
          if (swalResult.isConfirmed) handleActivateDomain();
        });
      } else {
        Swal.fire({
          title: 'Verificación incompleta',
          html: `<div style="text-align:left;font-size:13px"><p>${result.message || 'Algunos registros DNS no se encontraron.'}</p><p style="font-size:10px;color:#9ca3af;margin-top:6px">${result.records_passing}/${result.records_checked} registros correctos. Revisá la configuración DNS e intentá de nuevo.</p></div>`,
          icon: 'warning',
          confirmButtonText: 'Entendido',
          customClass: {
            popup: 'rounded-2xl',
            title: 'font-bold text-sm',
            confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
          },
          buttonsStyling: false,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al verificar DNS';
      Swal.fire({
        title: 'Error de verificación',
        text: msg,
        icon: 'error',
        confirmButtonText: 'Cerrar',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        },
        buttonsStyling: false,
      });
    } finally {
      setVerifying(false);
    }
  }, [domainData, radioId]);

  const handleActivateDomain = useCallback(async () => {
    if (!domainData?.domainId) return;

    try {
      const result = await setPrimaryDomain(radioId, domainData.domainId);
      setDomainData(result);

      Swal.fire({
        title: 'Dominio Activado',
        html: `<p style="font-size:13px;color:#374151"><strong>${domainData.domain}</strong> ahora redirige a tu radio.</p>`,
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al activar dominio';
      Swal.fire({
        title: 'Error',
        text: msg,
        icon: 'error',
        confirmButtonText: 'Cerrar',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        },
        buttonsStyling: false,
      });
    }
  }, [domainData, radioId]);

  const handleDeactivateDomain = useCallback(async () => {
    if (!domainData?.domainId) return;

    Swal.fire({
      title: 'Desactivar dominio',
      html: `<p style="font-size:13px;color:#374151">¿Querés desactivar <strong>${domainData.domain}</strong>?</p><p style="font-size:10px;color:#9ca3af;margin-top:4px">El QR permanente redirigirá a tu perfil de RadioFrecuenciaWeb. Podés reactivarlo cuando quieras.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-red-500 text-white',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then(async (swalResult) => {
      if (swalResult.isConfirmed) {
        try {
          const result = await removeDomain(radioId, domainData.domainId);
          setDomainData(result);

          Swal.fire({
            title: 'Dominio desactivado',
            html: `<p style="font-size:13px;color:#374151"><strong>${domainData.domain}</strong> fue desactivado. Los registros DNS siguen configurados.</p>`,
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
            customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' },
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Error al desactivar';
          Swal.fire({
            title: 'Error',
            text: msg,
            icon: 'error',
            confirmButtonText: 'Cerrar',
            customClass: {
              popup: 'rounded-2xl',
              title: 'font-bold text-sm',
              confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
            },
            buttonsStyling: false,
          });
        }
      }
    });
  }, [domainData, radioId]);

  const handleCopyInstructions = useCallback(() => {
    if (!domainData?.dnsRecords.length) return;
    const text = domainData.dnsRecords.map((r: DnsRecord) => `Tipo: ${r.type} | Host: ${r.host} | Valor: ${r.value} | TTL: ${r.ttl}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        title: 'Instrucciones copiadas',
        text: 'Pegalas en la configuración DNS de tu proveedor.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' },
      });
    }).catch(() => {
      Swal.fire({
        title: 'Instrucciones DNS',
        html: `<pre style="text-align:left;font-size:11px;background:#f3f4f6;padding:10px;border-radius:8px;overflow-x:auto">${text}</pre>`,
        icon: 'info',
        confirmButtonText: 'Cerrar',
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50' },
        buttonsStyling: false,
      });
    });
  }, [domainData]);

  const handleResetDomain = useCallback(() => {
    Swal.fire({
      title: 'Cambiar dominio',
      html: '<p style="font-size:12px;color:#6b7280;margin-bottom:10px;text-align:left">Esto desvinculará el dominio actual y requerirá nueva verificación DNS.</p>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Si, cambiar',
      cancelButtonText: 'Cancelar',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600',
      },
      buttonsStyling: false,
    }).then((swalResult) => {
      if (swalResult.isConfirmed) {
        setDomainData(null);
        setNewDomain('');
      }
    });
  }, []);

  // ── RENDER: Bloqueado ──

  if (!hasDomainFeature) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
          <i className="ri-lock-line text-amber-500 text-3xl"></i>
        </div>
        <h3 className="text-foreground-950 font-bold text-lg mb-2">Dominio Propio — Exclusivo Premium</h3>
        <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6 leading-relaxed">
          Tu plan actual es <strong className="text-foreground-800">{currentPlan.name}</strong>. Actualizá a Premium para vincular tu propio dominio.
        </p>
        <button
          onClick={openUpgradeModal}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-up-circle-line text-sm"></i>
          Ver planes Premium
        </button>
      </div>
    );
  }

  // ── RENDER: Loading ──

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-16 bg-background-200 rounded-2xl"></div>
        <div className="h-48 bg-background-200 rounded-2xl"></div>
        <div className="h-24 bg-background-200 rounded-2xl"></div>
      </div>
    );
  }

  const status = domainData?.status || 'PENDING';
  const statusInfo = DOMAIN_STATUS_LABELS[status];
  const sslInfo = domainData?.sslStatus ? SSL_STATUS_LABELS[domainData.sslStatus] : null;

  // ── RENDER: Sin dominio configurado ──

  if (!domainData?.domainId && status === 'PENDING') {
    return (
      <div className="space-y-6">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
          <h3 className="text-foreground-950 font-bold text-sm mb-1">Agregar dominio propio</h3>
          <p className="text-foreground-400 text-[11px] mb-4">Ingresá el dominio que querés vincular a tu radio (ej: miradio.com). No incluyas https://.</p>
          <div className="flex gap-3">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="miradio.com"
              className="flex-1 px-4 py-2.5 bg-white border border-background-200 rounded-xl text-sm outline-none focus:border-foreground-300 transition-colors"
              onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleRequestDomain(); }}
              disabled={submitting}
            />
            <button
              onClick={handleRequestDomain}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin text-sm"></i>Registrando...</>
              ) : (
                <><i className="ri-add-line text-sm"></i>Agregar</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!domainData) return null;

  // ── RENDER: Dominio configurado ──

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div className={`rounded-2xl p-4 flex items-center gap-3 border ${statusInfo.bgClass}`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${statusInfo.bgClass}`}>
          <i className={`${statusInfo.icon} ${statusInfo.colorClass} text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-foreground-800 text-sm font-bold">
            {status === 'ACTIVE' ? `${domainData.domain} — Activo` : statusInfo.label}
          </p>
          <p className="text-foreground-400 text-[11px]">
            {status === 'ACTIVE' && 'Tu dominio está redirigiendo correctamente.'}
            {status === 'VERIFIED' && 'DNS verificado. Activá el dominio para que redirija a tu radio.'}
            {status === 'DNS_REQUIRED' && 'Configurá los registros DNS en tu proveedor de dominio.'}
            {status === 'VERIFYING' && 'Verificando registros DNS...'}
            {status === 'FAILED' && (domainData.errorMessage || 'Hubo un problema. Revisá los registros DNS.')}
            {status === 'SUSPENDED' && 'Dominio suspendido. Contactá a soporte.'}
            {status === 'REMOVED' && 'Dominio eliminado. Los registros DNS pueden seguir activos.'}
          </p>
        </div>
      </div>

      {/* SSL Status (solo lectura) */}
      {sslInfo && domainData.sslStatus !== 'NOT_REQUESTED' && (
        <div className={`rounded-2xl p-4 flex items-center gap-3 border ${sslInfo.bgClass}`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${sslInfo.bgClass}`}>
            <i className={`${sslInfo.icon} ${sslInfo.colorClass} text-sm`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-foreground-800 text-xs font-bold">SSL: {sslInfo.label}</p>
            {domainData.sslExpiresAt && (
              <p className="text-foreground-400 text-[10px]">Expira: {new Date(domainData.sslExpiresAt).toLocaleDateString('es')}</p>
            )}
          </div>
        </div>
      )}

      {/* DNS Instructions */}
      {domainData.dnsRecords.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Instrucciones DNS</h3>
              <p className="text-foreground-400 text-[10px] mt-0.5">
                Configurá estos registros en tu proveedor de dominio (GoDaddy, Namecheap, Cloudflare, etc.)
              </p>
            </div>
            <button
              onClick={handleCopyInstructions}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-copy-line text-xs"></i>
              Copiar todo
            </button>
          </div>

          <div className="space-y-2">
            {domainData.dnsRecords.map((record, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-background-100">
                <span className="w-14 h-7 rounded-md bg-foreground-950 text-background-50 text-[10px] font-bold flex items-center justify-center shrink-0">
                  {record.type}
                </span>
                <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase">Host</p>
                    <p className="text-foreground-800 text-xs font-mono truncate">{record.host}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase">Valor</p>
                    <p className="text-foreground-800 text-xs font-mono truncate">{record.value}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(record.value).then(() => {
                      Swal.fire({ title: 'Copiado', text: record.value, icon: 'success', timer: 1500, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' } });
                    });
                  }}
                  className="w-8 h-8 rounded-lg bg-white border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-50 shrink-0 whitespace-nowrap"
                  title="Copiar valor"
                >
                  <i className="ri-file-copy-line text-foreground-400 text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <h3 className="text-foreground-950 font-bold text-sm mb-4">Acciones</h3>
        <div className="flex flex-wrap gap-2">
          {(status === 'DNS_REQUIRED' || status === 'FAILED') && (
            <button
              onClick={handleVerifyDns}
              disabled={verifying}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {verifying ? (
                <><i className="ri-loader-4-line animate-spin text-xs"></i>Verificando...</>
              ) : (
                <><i className="ri-search-line text-xs"></i>Verificar DNS</>
              )}
            </button>
          )}
          {status === 'VERIFIED' && (
            <button
              onClick={handleActivateDomain}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line text-xs"></i>
              Activar dominio
            </button>
          )}
          {status === 'ACTIVE' && (
            <button
              onClick={handleDeactivateDomain}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line text-xs"></i>
              Desactivar dominio
            </button>
          )}
          {(status === 'VERIFIED' || status === 'ACTIVE') && (
            <button
              onClick={handleVerifyDns}
              disabled={verifying}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              <i className="ri-refresh-line text-xs"></i>
              Re-verificar DNS
            </button>
          )}
          <button
            onClick={handleCopyInstructions}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line text-xs"></i>
            Copiar instrucciones
          </button>
          <button
            onClick={handleResetDomain}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-xs"></i>
            Cambiar dominio
          </button>
        </div>
      </div>

      {/* Verification history */}
      {domainData.verificationHistory.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
          <h3 className="text-foreground-950 font-bold text-sm mb-4">Historial de verificación</h3>
          <div className="space-y-2">
            {domainData.verificationHistory.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-100">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${entry.result === 'success' ? 'bg-emerald-100' : 'bg-red-50'}`}>
                  <i className={`text-sm ${entry.result === 'success' ? 'ri-check-line text-emerald-600' : 'ri-close-line text-red-500'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-foreground-800 text-xs">{entry.message}</p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">{new Date(entry.timestamp).toLocaleString('es', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${entry.result === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                  {entry.result === 'success' ? 'Éxito' : 'Falló'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500"></i>
          <p className="text-red-700 text-xs">{error}</p>
        </div>
      )}
    </div>
  );
}